import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, CirclePlus } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { storeExpenseTypeData } from "@/Api/expenseTypes";
import { selectExpenseCategoriesData } from "@/Api/expenseCategories";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExpenseCategoryProps } from "@/types/expenseCategory";
import { SearchableSelect } from "@/components/ui/searchable-select";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  description: z.string().optional(),
  expense_category_id: z.number({ message: "*requerido" }),
  is_recurrence: z.boolean().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const AddExpenseTypeBtn = ({ updateFront }: { updateFront: any }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [categories, setCategories] = useState<ExpenseCategoryProps[]>([]);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      expense_category_id: undefined,
      is_recurrence: false,
    },
  });

  useEffect(() => {
    const fetchCategories = async () => {
      if (isOpen) {
        try {
          const { data } = await selectExpenseCategoriesData();
          if (data?.response) {
            setCategories(data.response);
          }
        } catch (error) {
          console.error("Erro ao carregar categorias:", error);
        }
      }
    };

    fetchCategories();
  }, [isOpen]);

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");

    const payload = {
      name: data.name,
      description: data.description || "",
      is_recurrence: data.is_recurrence || false,
      expense_category_id: data.expense_category_id,
    };

    const { data: apiResponse, ok } = await storeExpenseTypeData(payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront((prev) => {
      return {
        ...prev,
        data: [
          {
            ...apiResponse?.response,
            is_active: 1,
          },
          ...prev?.data,
        ],
      };
    });

    toast({
      description: apiResponse?.message || "Adicionado com sucesso!",
      action: <Check color="#1edc2b" />,
    });

    form.reset();
    setFormState("default");
    setIsOpen(false);
  };

  const selectedCategory = categories.find(
    (category) => category.id == form.watch("expense_category_id")
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Tipo de Despesa
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Tipo de Despesa
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-full">
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-3"
          >
            <Form.Row>
              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Insira um nome" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            <Form.Row>
              <Form.Field name="description">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Descrição <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Textarea
                        placeholder="Descrição do tipo de despesa"
                        className="resize-none"
                        {...field}
                        value={field.value || ""}
                      />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            <Form.Row>
              <Form.Field name="expense_category_id">
                {({ field }) => (
                  <Form.Item className="flex flex-col">
                    <Form.Label>
                      Categoria <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <SearchableSelect
                        items={categories}
                        searchFields={["name"]}
                        displayField="name"
                        placeholder="Buscar categoria..."
                        emptyMessage="Nenhuma categoria encontrada."
                        value={selectedCategory}
                        onSelect={(category) => field.onChange(category.id)}
                        getItemId={(item) => item.id}
                      />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            <DialogFooter>
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Adicionando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Adicionar"}
                {formState === "error" && "Erro ao adicionar"}
              </Button>
            </DialogFooter>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
